import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ReportResource, Role } from '@/types/resources';
import { api } from '@/utils/api';

import ReportForm from './ReportForm';

const Name = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  border-radius: 2.66667px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  margin-right: 16px;
  flex-shrink: 0;
  position: relative;
`;

const IconInner = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const Svg = styled.svg`
  color: ${({ theme }) => theme.colors.black};
`;

const Tr = styled.tr`
  cursor: pointer;
  > td {
    transition: 0.2s ease-out background-color;
  }
  &:hover {
    > td {
      background: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

const LockSvg = styled.svg`
  position: absolute;
  top: -6px;
  right: -6px;
`;

interface Props {
  report: ReportResource;
  userRole: Role;
  isGroupRestricted: boolean;
}

const ReportRow: FC<Props> = ({ report, isGroupRestricted }) => {
  const alerts = useAlerts();

  const [showEditForm, setShowEditForm] = useState(false);

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this report?',
        report.name,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/reports/${report.id}`);

      queryClient.invalidateQueries(['reports']);
      queryClient.invalidateQueries(['reports_groups']);

      alerts.success('Report was successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  const onClick = () => {
    window.open(report.embedUrl, '_blank')?.focus();
  };

  return (
    <Tr onClick={onClick}>
      <Td>
        <Name>
          {report.isRestricted && (
            <Icon>
              <LockSvg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_6396_148668)">
                  <circle cx="7" cy="7" r="6.5" fill="white" stroke="white" />
                  <circle cx="7" cy="7" r="6" fill="#E9E9E9" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.57192 5.57502C8.57192 4.70515 7.86674 4 6.9969 4C6.12705 4 5.42188 4.70513 5.42188 5.57502V6.925H5.8719V5.57502C5.8719 4.9537 6.37555 4.45 6.99687 4.45C7.61819 4.45 8.12184 4.9537 8.12184 5.57502V6.925H8.57187L8.57192 5.57502Z"
                    fill="#222222"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.75 6.70129V8.94865C4.75 9.19792 4.95214 9.39994 5.19795 9.39994H8.80205C9.04942 9.39994 9.25 9.1981 9.25 8.94865V6.70129C9.25 6.45203 9.04786 6.25 8.80205 6.25H5.19795C4.95058 6.25 4.75 6.4518 4.75 6.70129Z"
                    fill="#222222"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6396_148668">
                    <rect width="14" height="14" fill="white" />
                  </clipPath>
                </defs>
              </LockSvg>
              <IconInner>
                <Svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 13H2V3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 6L10 9.5L6 6.5L2 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              </IconInner>
            </Icon>
          )}
          <Text $type="smallBold">{report.name}</Text>
        </Name>
      </Td>
      <Td>{report.note}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setShowEditForm(true);
              }}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditForm && (
            <ReportForm
              editableReport={report}
              groupId={report.groupId}
              onClose={() => setShowEditForm(false)}
              isGroupRestricted={isGroupRestricted}
            />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default ReportRow;
